import type { KeyboardEvent, Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EPropertyEditTypes, PropertySettings } from 'state/selectedTemplate/selectedTemplateStore';
import { getPropertyEditType } from 'utils/property/getPropertyEditType';
import { validatePropertyValue } from 'utils/property/validatePropertyValue';
import type { Nullable } from 'utils/values';
import { hasValue } from 'utils/values';

/**
 * What the input observed about the change, beyond the value itself.
 *
 * `badInput` mirrors `ValidityState.badInput`: a `type='number'` field runs the HTML value
 * sanitisation algorithm, so text it cannot parse as a float (`123-123`) is reported as an empty
 * value while the typed characters stay on screen. Without this flag the editor would read that as
 * "clear this property" — no message, and the value already stored deleted on blur.
 */
export interface PropertyValueChangeMeta {
  badInput?: boolean;
}

export type PropertyValueChangeHandler = (value: string, meta?: PropertyValueChangeMeta) => void;

export interface UsePropertyValueEditorOptions {
  settings: Nullable<PropertySettings>;
  /** The current server value. */
  value: unknown;
  /**
   * Called on blur with the new value, only when it changed and passes validation. A returned
   * promise is awaited only to notice failure: a rejected commit releases the duplicate-write guard
   * so the same value can be submitted again.
   */
  onCommit: (value: string) => unknown;
  /** Runs after every blur, committed or not (the legacy panel releases its grid cell here). */
  onAfterBlur?: () => void;
  /** Runs on focus, after the editor has recorded it. */
  onFocusChange?: () => void;
}

export interface PropertyValueEditor {
  editType: EPropertyEditTypes;
  editValue: string;
  focused: boolean;
  /** Validation message for the current edit value, or null when it is valid. */
  errorMessage: Nullable<string>;
  onValueChange: PropertyValueChangeHandler;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  inputRef: Ref<HTMLInputElement>;
}

/**
 * The edit-state machine shared by the node-details panels: local edit value, focus, validation, and
 * the decision of whether a blur should write. The data layer stays with the caller — this hook only
 * calls `onCommit` when there is something valid and changed to save.
 */
export function usePropertyValueEditor({
  settings,
  value,
  onCommit,
  onAfterBlur,
  onFocusChange
}: UsePropertyValueEditorOptions): PropertyValueEditor {
  const editType = getPropertyEditType(settings);

  const [editValue, setEditValue] = useState<string>(() => toEditString(value));
  const [focused, setFocused] = useState<boolean>(false);
  // Whether the user has typed in this field. Validation describes what was *typed*, but `editValue`
  // is also seeded from — and resynced to — the server value, which can itself be invalid (a bulk
  // import, a direct API write, a limit lowered after the fact). Reporting that on open would be a
  // false alarm about an edit nobody made, for a value no blur would have written anyway.
  const [edited, setEdited] = useState<boolean>(false);
  // Whether the input is currently holding characters it could not parse. Not derivable from
  // `editValue`, which such input empties — see `PropertyValueChangeMeta`.
  const [badInput, setBadInput] = useState<boolean>(false);

  // The value the user was last shown. `value` can move underneath a focused field (a live SignalR
  // patch), so committing is gated on this rather than on `value` — otherwise merely focusing and
  // blurring a field would write the stale value back over someone else's edit.
  const lastSyncedValue = useRef<string>(toEditString(value));
  // Mirrors editValue: Escape and Enter blur the input synchronously, so onBlur would otherwise read
  // the pre-keydown state from its closure.
  const editValueRef = useRef<string>(editValue);
  // Mirrors badInput, for the same reason.
  const badInputRef = useRef<boolean>(false);
  // What this field last wrote. A commit does not round-trip into `value` immediately, so without
  // this a second blur with no typing would write the identical value again — a duplicate update
  // event and audit entry. Not folded into `lastSyncedValue`, which has to keep describing what the
  // server last said for the resync guard below to work.
  const lastCommittedValue = useRef<Nullable<string>>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  // Some widgets (e.g. MUI's DatePicker) proxy blur through their own onClose rather than a native
  // DOM blur event, which isn't guaranteed to fire if the component unmounts while still focused/open.
  // Firing onAfterBlur once more on unmount guards against a caller's cleanup (e.g. releasing a grid
  // "being edited" lock) getting stuck forever; it's a no-op to call it twice.
  const onAfterBlurRef = useRef(onAfterBlur);
  onAfterBlurRef.current = onAfterBlur;

  useEffect(() => {
    return () => {
      onAfterBlurRef.current?.();
    };
  }, []);

  // The flag travels with the value so the two can never disagree; a caller replacing the value with
  // a known-parsable one just leaves it to its default. Such a caller wants `resetValue` below, which
  // also deals with what is left on screen.
  const setValue = useCallback((nextValue: string, nextBadInput = false) => {
    editValueRef.current = nextValue;
    badInputRef.current = nextBadInput;
    setEditValue(nextValue);
    setBadInput(nextBadInput);
  }, []);

  /**
   * Replaces whatever the field holds, unparsable characters included. React will not do it: it
   * writes a controlled value into the DOM only when it differs from what the node reports, and a
   * node holding unparsable characters already reports the empty string — so those characters would
   * stay on screen with nothing behind them, still failing the node's own validity. Escape needs it
   * synchronously, since it blurs the input in the same tick, before React would have re-rendered.
   *
   * Only for a caller discarding the field's contents outright. Typing must not come through here:
   * mid-word a value is unparsable all the time (`1.` on the way to `1.5`, `-` before a negative
   * number), and resetting the node then would delete the characters as they were typed.
   */
  const resetValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue);

      // Not every input leaves a DOM node here despite the ref's type: MUI's Select (the picklist)
      // installs an imperative handle instead, which is why the blur below is an optional call too.
      // Only a `type='number'` element can be holding unparsable characters in the first place.
      const input = inputRef.current;
      if (input instanceof HTMLInputElement && input.validity.badInput) {
        input.value = nextValue;
      }
    },
    [setValue]
  );

  const onValueChange = useCallback<PropertyValueChangeHandler>(
    (nextValue, meta) => {
      const nextBadInput = meta?.badInput === true;

      // Blur re-reports whatever the input currently holds (see the number field in
      // `PropertyValueInput`), which for an untouched field is the server value. Counting that as an
      // edit would start reporting a stored value's own problems as if the user had typed them.
      if (nextValue !== editValueRef.current || nextBadInput !== badInputRef.current) {
        setEdited(true);
      }

      setValue(nextValue, nextBadInput);
    },
    [setValue]
  );

  useEffect(() => {
    // Skip resync while focused so a live patch does not clobber in-progress typing.
    if (focused) return;

    const syncedValue = toEditString(value);

    // Nothing actually changed server-side — most commonly right after this same field's own blur,
    // before its own commit has round-tripped back into `value`. Resyncing anyway would momentarily
    // stomp the just-typed value with the (still stale) old one, flashing old-then-new on screen.
    if (syncedValue === lastSyncedValue.current) return;

    lastSyncedValue.current = syncedValue;
    // The server has moved on, so an earlier commit no longer says anything about what is worth
    // writing next — retyping that same value must be allowed to write again.
    lastCommittedValue.current = null;
    setEdited(false);
    resetValue(syncedValue);
  }, [value, focused, resetValue]);

  const errorMessage = useMemo(
    () => (edited ? validatePropertyValue(editValue, settings, { badInput }) : null),
    [badInput, edited, editValue, settings]
  );

  const onBlur = useCallback(() => {
    setFocused(false);

    // Validated from the ref rather than from `errorMessage`, which trails a tick behind: widgets
    // that report a value and blur within the same event (the DatePicker's onChange -> onClose,
    // Ownership's onSelectionChange -> onBlur) would otherwise be judged on the value before last.
    const valueForSubmit = editValueRef.current;
    const isValid = validatePropertyValue(valueForSubmit, settings, { badInput: badInputRef.current }) === null;
    const hasChanged = lastSyncedValue.current !== valueForSubmit && lastCommittedValue.current !== valueForSubmit;

    if (isValid && hasChanged) {
      lastCommittedValue.current = valueForSubmit;

      // A failed write never round-trips into `value`, so the resync above would never clear the
      // guard: the field would keep showing an unsaved value that no further blur could submit.
      // Only release it if nothing newer has been committed since. `Promise.resolve` covers a
      // caller that returns nothing as well as one that returns a promise from another realm.
      Promise.resolve(onCommit(valueForSubmit)).catch(() => {
        if (lastCommittedValue.current === valueForSubmit) {
          lastCommittedValue.current = null;
        }
      });
    }

    onAfterBlur?.();
  }, [onAfterBlur, onCommit, settings]);

  const onFocus = useCallback(() => {
    setFocused(true);
    onFocusChange?.();
  }, [onFocusChange]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        // Escape discards the edit and adopts whatever the server value is now, including a live
        // patch that arrived while typing — so the following blur must not treat it as a change.
        const syncedValue = toEditString(value);
        lastSyncedValue.current = syncedValue;
        setEdited(false);
        resetValue(syncedValue);
        inputRef.current?.blur?.();
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        inputRef.current?.blur?.();
      }
    },
    [resetValue, value]
  );

  return {
    editType,
    editValue,
    focused,
    errorMessage,
    onValueChange,
    onBlur,
    onFocus,
    onKeyDown,
    inputRef
  };
}

/**
 * The controlled MUI inputs must never receive null, which triggers "value should not be null" and
 * uncontrolled→controlled warnings.
 */
function toEditString(value: unknown): string {
  return hasValue(value) ? String(value) : '';
}
